import React, { useCallback, useEffect, useState } from 'react';
import { FileText, CheckCircle2, XCircle, MessageSquare, PenLine, Layers } from 'lucide-react';
import {
  fetchPublishedBidsForAccount,
  submitClientBidResponse,
} from '../../services/alaCarteBidService';
import { startBidContractSigning } from '../../services/bidContractService';
import {
  CLIENT_RESPONSE_STATUS,
  CLIENT_RESPONSE_LABELS,
  BID_CONTRACT_STATUS,
} from '../../utils/alaCarteBid';

function formatMoney(raw) {
  const n = parseFloat(String(raw));
  if (!Number.isFinite(n)) return '—';
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function responsePillClass(status) {
  switch (status) {
    case CLIENT_RESPONSE_STATUS.accepted:
      return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200';
    case CLIENT_RESPONSE_STATUS.declined:
      return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
    case CLIENT_RESPONSE_STATUS.countered:
      return 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100';
    default:
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';
  }
}

/**
 * Published à la carte quotes awaiting the client's decision (accept / decline / counter).
 * Renders nothing when the account has no published quotes.
 *
 * @param {{ accountId: string, currentUser: object, darkMode?: boolean }} props
 */
export default function PortalAlaCarteQuotes({ accountId, currentUser }) {
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [submittingId, setSubmittingId] = useState(null);
  const [signingId, setSigningId] = useState(null);
  const [counterOpenId, setCounterOpenId] = useState(null);
  const [counterPrice, setCounterPrice] = useState('');
  const [counterNote, setCounterNote] = useState('');

  const load = useCallback(async () => {
    if (!accountId) {
      setQuotes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const list = await fetchPublishedBidsForAccount(accountId);
      setQuotes(list);
    } catch (e) {
      console.error('PortalAlaCarteQuotes: load', e);
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    load();
  }, [load]);

  const launchSigning = async (quote) => {
    setSigningId(quote.id);
    try {
      const { signingUrl } = await startBidContractSigning(quote.id);
      window.location.href = signingUrl;
    } catch (e) {
      console.error('startBidContractSigning', e);
      setSigningId(null);
      alert(e?.message || 'Could not start contract signing. Please try again.');
      await load();
    }
  };

  const respond = async (quote, status) => {
    if (status === CLIENT_RESPONSE_STATUS.countered) {
      const n = parseFloat(String(counterPrice));
      if (!Number.isFinite(n) || n < 0) {
        alert('Please enter a valid counter price.');
        return;
      }
    }
    if (status === CLIENT_RESPONSE_STATUS.declined && !window.confirm('Decline this quote?')) return;
    setSubmittingId(quote.id);
    try {
      await submitClientBidResponse({
        bidId: quote.id,
        status,
        counterPrice: status === CLIENT_RESPONSE_STATUS.countered ? counterPrice : '',
        note: status === CLIENT_RESPONSE_STATUS.countered ? counterNote : '',
        respondedByUid: currentUser?.uid || '',
        respondedByName: currentUser?.displayName || currentUser?.email || '',
      });
      setCounterOpenId(null);
      setCounterPrice('');
      setCounterNote('');
      if (status === CLIENT_RESPONSE_STATUS.accepted) {
        // Acceptance flows straight into the DocuSign signing ceremony.
        await launchSigning(quote);
        return;
      }
      await load();
    } catch (e) {
      console.error(e);
      alert(e?.message || 'Could not submit your response. Please try again.');
    } finally {
      setSubmittingId(null);
    }
  };

  if (loading || quotes.length === 0) return null;

  return (
    <div className="mb-10">
      <h2 className="mb-1 text-xl font-semibold text-gray-800 dark:text-white">Quotes for your review</h2>
      <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
        Custom pricing the BBA team has prepared for you. Accept to move forward, or send a counter.
      </p>
      <div className="space-y-4">
        {quotes.map((quote) => {
          const responded = !!quote.clientResponse?.status;
          const busy = submittingId === quote.id;
          const counterOpen = counterOpenId === quote.id;
          return (
            <div
              key={quote.id}
              className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <span className="text-3xl">{quote.icon || '📦'}</span>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{quote.name}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{quote.department}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatMoney(quote.clientListPrice)}
                  </div>
                  <span
                    className={`mt-1 inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${responsePillClass(
                      quote.clientResponse?.status
                    )}`}
                  >
                    {responded
                      ? CLIENT_RESPONSE_LABELS[quote.clientResponse.status]
                      : 'Awaiting your response'}
                  </span>
                </div>
              </div>

              {quote.scope && (
                <div className="mt-4">
                  <h4 className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <FileText className="h-3.5 w-3.5" />
                    Scope of work
                  </h4>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{quote.scope}</p>
                </div>
              )}

              {/* Packages list their bundled services; the mirrored deliverable rows would just repeat them. */}
              {quote.isPackage && quote.packageItems.length > 0 ? (
                <div className="mt-4">
                  <h4 className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <Layers className="h-3.5 w-3.5" />
                    What&apos;s included
                  </h4>
                  <ul className="mt-2 space-y-1">
                    {quote.packageItems.map((item, i) => (
                      <li key={i} className="flex items-baseline gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <span className="leading-none">{item.icon || '📦'}</span>
                        <span>
                          {item.quantity > 1 ? `${item.quantity} × ` : ''}
                          {item.name}
                          {item.notes ? (
                            <span className="text-gray-500 dark:text-gray-400"> — {item.notes}</span>
                          ) : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : Array.isArray(quote.deliverableRows) && quote.deliverableRows.length > 0 ? (
                <ul className="mt-3 list-inside list-disc text-sm text-gray-700 dark:text-gray-300">
                  {quote.deliverableRows
                    .filter((r) => String(r?.quantity || '').trim() || String(r?.description || '').trim())
                    .map((r, i) => (
                      <li key={i}>
                        {r.quantity ? `${r.quantity} × ` : ''}
                        {r.description || ''}
                      </li>
                    ))}
                </ul>
              ) : null}

              {responded ? (
                <div className="mt-4 rounded-lg bg-gray-50 p-3 text-sm text-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
                  {quote.clientResponse.status === CLIENT_RESPONSE_STATUS.accepted && (
                    <div>
                      {quote.contract?.status === BID_CONTRACT_STATUS.completed ? (
                        <p className="flex items-center gap-1.5 font-medium text-green-700 dark:text-green-300">
                          <CheckCircle2 className="h-4 w-4" />
                          Contract signed. The BBA team is getting started on this work.
                        </p>
                      ) : quote.contract?.status === BID_CONTRACT_STATUS.clientSigned ? (
                        <p>
                          You&apos;ve signed the contract — it&apos;s now awaiting countersignature from the BBA
                          team. No further action is needed.
                        </p>
                      ) : (
                        <div>
                          <p>
                            You accepted this quote.
                            {quote.contract?.status === BID_CONTRACT_STATUS.sent
                              ? ' Your contract is ready to sign.'
                              : ' The next step is signing your contract.'}
                          </p>
                          <button
                            type="button"
                            disabled={signingId === quote.id}
                            onClick={() => launchSigning(quote)}
                            className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                          >
                            <PenLine className="h-4 w-4" />
                            {signingId === quote.id ? 'Preparing your contract…' : 'Review & sign contract'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {quote.clientResponse.status === CLIENT_RESPONSE_STATUS.declined && (
                    <p>You declined this quote. Reach out to your BBA contact if anything changes.</p>
                  )}
                  {quote.clientResponse.status === CLIENT_RESPONSE_STATUS.countered && (
                    <p>
                      You proposed {formatMoney(quote.clientResponse.counterPrice)}. The BBA team is reviewing your
                      counter and will get back to you.
                    </p>
                  )}
                </div>
              ) : (
                <div className="mt-5 border-t border-gray-200 pt-4 dark:border-gray-700">
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => respond(quote, CLIENT_RESPONSE_STATUS.accepted)}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      {busy && signingId === quote.id ? 'Preparing contract…' : 'Accept quote'}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setCounterOpenId(counterOpen ? null : quote.id);
                        setCounterPrice('');
                        setCounterNote('');
                      }}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-amber-400 px-4 py-2 text-sm font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/20"
                    >
                      <MessageSquare className="h-4 w-4" />
                      {counterOpen ? 'Cancel counter' : 'Counter offer'}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => respond(quote, CLIENT_RESPONSE_STATUS.declined)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-900/40"
                    >
                      <XCircle className="h-4 w-4" />
                      Decline
                    </button>
                  </div>

                  {counterOpen && (
                    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/60 p-4 dark:border-amber-900/50 dark:bg-amber-900/10">
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Your proposed price (USD)
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={counterPrice}
                            onChange={(e) => setCounterPrice(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                            placeholder="0.00"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Note (optional)
                          </label>
                          <textarea
                            rows={2}
                            value={counterNote}
                            onChange={(e) => setCounterNote(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                            placeholder="Anything you'd like the team to know about your counter"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => respond(quote, CLIENT_RESPONSE_STATUS.countered)}
                        className="mt-3 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                      >
                        {busy ? 'Sending…' : 'Send counter offer'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
